"use client"

import { useEffect, useState } from "react"
import {
  CheckCircle2,
  ChefHat,
  MapPin,
  PartyPopper,
  ShieldCheck,
  Star,
  Truck,
  Utensils,
} from "lucide-react"
import courierScreen from "../../assets/home_screen_courier.jpeg"
import dineZoneScreen from "../../assets/home_screen_dinezone.jpeg"
import partyZoneScreen from "../../assets/home_screen_patyzone.jpeg"
import cateringScreen from "../../assets/home_screen_catering.jpeg"
import "./HeroSection.css"

const services = [
  { label: "Food Delivery", icon: Utensils },
  { label: "DineZone", icon: Star },
  { label: "PartyZone", icon: PartyPopper },
  { label: "Courier", icon: Truck },
  { label: "Catering", icon: ChefHat },
]

const appScreens = [
  { src: courierScreen, label: "Courier" },
  { src: dineZoneScreen, label: "DineZone" },
  { src: partyZoneScreen, label: "PartyZone" },
  { src: cateringScreen, label: "Catering" },
]

export default function HeroSection() {
  const [activeScreenIndex, setActiveScreenIndex] = useState(0)

  useEffect(() => {
    appScreens.forEach(({ src }) => {
      const image = new Image()
      image.src = src
    })

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduceMotion) return undefined

    const interval = window.setInterval(() => {
      setActiveScreenIndex((current) => (current + 1) % appScreens.length)
    }, 3800)

    return () => window.clearInterval(interval)
  }, [])

  const activeScreen = appScreens[activeScreenIndex]

  return (
    <section className="hero-section">
      <div className="hero-grid-pattern" aria-hidden="true" />
      <div className="hero-glow hero-glow-one" aria-hidden="true" />
      <div className="hero-glow hero-glow-two" aria-hidden="true" />

      <div className="container hero-inner">
        <div className="hero-copy">
          <div className="hero-live-badge">
            <span className="hero-live-dot" />
            <MapPin size={15} aria-hidden="true" />
            Now live in Delhi
          </div>

          <h1 className="hero-title">
            One app for the way
            <span>your city moves.</span>
          </h1>

          <p className="hero-description">
            Order food, discover restaurants, plan celebrations, book catering
            and send couriers—all through one simple Katchit experience.
          </p>

          <div className="hero-store-row" aria-label="Download Katchit">
            <a
              href="https://play.google.com/store/apps/details?id=com.katchit.customer_app"
              aria-label="Open Katchit on Google Play"
            >
              <img src="/assets/play_store.avif" alt="Get Katchit on Google Play" />
            </a>
            <a
              href="https://apps.apple.com/in/app/katchit-customer/id6785157187"
              aria-label="Open Katchit on the App Store"
            >
              <img src="/assets/app_store.avif" alt="Download Katchit on the App Store" />
            </a>
          </div>

          <div className="hero-trust-row">
            <span>
              <CheckCircle2 size={17} aria-hidden="true" />
              Easy to use
            </span>
            <span>
              <ShieldCheck size={17} aria-hidden="true" />
              Safe & secure
            </span>
            <span>
              <Star size={17} aria-hidden="true" />
              Local favourites
            </span>
          </div>
        </div>

        <div className="hero-visual" aria-label="Katchit app preview">
          <div className="hero-visual-ring hero-ring-one" aria-hidden="true" />
          <div className="hero-visual-ring hero-ring-two" aria-hidden="true" />

          <div className="hero-service-cloud" aria-label="Services available on Katchit">
            {services.map(({ label, icon: Icon }, index) => (
              <div className={`hero-service-pill hero-service-${index + 1}`} key={label}>
                <span>
                  <Icon size={17} aria-hidden="true" />
                </span>
                {label}
              </div>
            ))}
          </div>

          <div className="hero-phone-stage">
            <div className="hero-phone-halo" aria-hidden="true" />
            <div className="hero-device-frame">
              <span className="hero-device-camera" aria-hidden="true" />
              <img
                key={activeScreen.src}
                src={activeScreen.src}
                alt={`Katchit ${activeScreen.label} app home screen`}
                className="hero-screen-image"
              />
            </div>
            <div className="hero-screen-progress" aria-label={`${activeScreen.label} screen shown`}>
              {appScreens.map((screen, index) => (
                <button
                  key={screen.label}
                  type="button"
                  className={index === activeScreenIndex ? "is-active" : ""}
                  aria-label={`Show ${screen.label} screen`}
                  aria-pressed={index === activeScreenIndex}
                  onClick={() => setActiveScreenIndex(index)}
                />
              ))}
            </div>
          </div>

          <div className="hero-location-card">
            <span className="hero-location-icon">
              <MapPin size={19} aria-hidden="true" />
            </span>
            <div>
              <strong>Delivering across Delhi</strong>
              <small>More cities coming soon</small>
            </div>
          </div>

          <div className="hero-rating-card">
            <div className="hero-rating-stars" aria-hidden="true">
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
            </div>
            <strong>Made for everyday plans</strong>
          </div>
        </div>
      </div>

      <div className="hero-bottom-curve" aria-hidden="true" />
    </section>
  )
}
